import { forwardRef, useImperativeHandle } from 'react';

import {
  Alert,
  AlertDescription,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  Heading,
  HStack,
  Text,
  VStack,
} from '@chakra-ui/react';

import { formatFileSize, isDefined } from '@enterprise/core';

import { ArrowRight } from '#icons/arrow-right.tsx';
import { CircleExclamation } from '#icons/circle-exclamation.tsx';
import { Upload } from '#icons/upload.tsx';
import { DefaultToolTip } from '#tool-tip/default-tool-tip.tsx';

import type { UploaderHandle } from './types/uploader-handle.type';
import type { UploaderProps } from './types/uploader.props';

import { StagedFileRow } from './components/staged-file-row';
import { DEFAULT_THRESHOLD_BYTES } from './constants/default-threshold';
import { useUploaderConfiguration } from './hooks/uploader-configuration.hook';

/**
 * A file uploader component with support for drag-and-drop, file type restrictions,
 * automatic compression for large files, and customizable filename suffixes.
 *
 * Features:
 * - Drag-and-drop or click to select files.
 * - Displays accepted file extensions.
 * - Shows staged files with progress and suffix editing.
 * - Automatically compresses files above a configurable size threshold.
 * - Displays upload progress, compression summary, and error/rejection alerts.
 * - Provides imperative handle for reset and upload actions.
 *
 * @component
 * @param {UploaderProps} props - The uploader configuration and event handlers.
 * @param {string[]} [props.allowedExtensions] - List of allowed file extensions (without dot).
 * @param {number} [props.autoCompressThresholdBytes] - Minimum file size (in bytes) to trigger automatic compression.
 * @param {boolean} [props.disabled] - Disables the uploader UI and actions.
 * @param {string} [props.filenameSuffixLabel] - Label for the filename suffix input.
 * @param {(file: File) => string} [props.getDefaultSuffix] - Function to provide a default suffix for each file.
 * @param {string[]} [props.nonCompressibleExtensions] - Extensions that should not be compressed.
 * @param {(files: File[]) => void} [props.onFilesReady] - Callback when files are ready for upload.
 * @param {string} [props.outputFilenameTemplate] - Template for output filenames.
 * @param {boolean} [props.showSuffixInput] - Whether to show the suffix input for each file.
 * @param {boolean} [props.showUploadButton] - Whether to show the upload button.
 * @param {React.Ref<UploaderHandle>} ref - Ref object exposing imperative methods: `reset` and `upload`.
 *
 * @returns {JSX.Element} The rendered uploader UI.
 *
 * @example
 * ```tsx
 * const uploaderRef = useRef<UploaderHandle>(null);
 *
 * <Uploader
 *   allowedExtensions={['csv', 'txt']}
 *   autoCompressThresholdBytes={1024 * 1024}
 *   onFilesReady={handleFiles}
 *   ref={uploaderRef}
 * />
 * ```
 */
export const Uploader = forwardRef<UploaderHandle, UploaderProps>(
  (
    {
      allowedExtensions = [],
      autoCompressThresholdBytes = DEFAULT_THRESHOLD_BYTES,
      disabled = false,
      filenameSuffixLabel,
      getDefaultSuffix,
      nonCompressibleExtensions = [],
      onFilesReady,
      outputFilenameTemplate,
      showSuffixInput = isDefined(outputFilenameTemplate),
      showUploadButton = true,
    },
    ref
  ) => {
    const {
      acceptAttr,
      allowedSet,
      cancelUpload,
      compressCount,
      compressionSummary,
      dragging,
      dropZoneBlocked,
      error,
      handleSuffixChange,
      handleUpload,
      hasCompressibleFiles,
      hasStaged,
      inputRef,
      isCancelled,
      isCompressing,
      isDone,
      isIdle,
      onDrop,
      onInputChange,
      processed,
      progressMap,
      rawCount,
      rejectedNames,
      removeStaged,
      reset,
      setDragging,
      staged,
      status,
      thresholdLabel,
      uploadBlocked,
    } = useUploaderConfiguration({
      allowedExtensions,
      autoCompressThresholdBytes,
      disabled,
      getDefaultSuffix,
      nonCompressibleExtensions,
      onFilesReady,
      outputFilenameTemplate,
    });

    useImperativeHandle(ref, () => {
      return {
        reset,
        upload: handleUpload,
      };
    }, [handleUpload, reset]);

    return (
      <Box
        bg="white"
        border="1px solid"
        borderColor="gray.300"
        borderRadius="md"
        opacity={disabled ? 0.6 : 1}
        p={8}
        transition="opacity 0.2s"
        w="full">
        <Flex
          align="center"
          justify="space-between"
          mb={4}>
          <Heading
            color="gray.800"
            fontSize="xl"
            fontWeight="semibold"
            letterSpacing="tight">
            File Uploader
          </Heading>
          <DefaultToolTip
            fontSize="xs"
            label={thresholdLabel}
            placement="left">
            <Badge
              borderColor="gray.600"
              borderRadius="full"
              borderWidth={1}
              colorScheme={Number.isFinite(autoCompressThresholdBytes) ? 'green' : 'blue'}
              cursor="default"
              fontSize="sm"
              px={2}
              py={0.5}
              variant="subtle">
              {Number.isFinite(autoCompressThresholdBytes)
                ? `Gzip ≥ ${formatFileSize(autoCompressThresholdBytes)}`
                : 'No compression'}
            </Badge>
          </DefaultToolTip>
        </Flex>

        {isDefined(allowedSet) ? (
          <Text
            color="gray.500"
            fontSize="sm"
            mb={4}>
            Accepted:{' '}
            {[...allowedSet]
              .map((e) => {
                return `.${e}`;
              })
              .join(', ')}
          </Text>
        ) : (
          <Box mb={4} />
        )}

        {!isDone && (
          <Box
            _hover={!dropZoneBlocked ? { bg: 'blue.50', borderColor: 'blue.400' } : {}}
            bg={dragging ? 'blue.50' : 'gray.50'}
            border="1px dashed"
            borderColor={dragging ? 'blue.400' : 'gray.300'}
            borderRadius="xl"
            cursor={dropZoneBlocked ? 'not-allowed' : 'pointer'}
            mb={4}
            onClick={() => {
              return !dropZoneBlocked && inputRef.current?.click();
            }}
            onDragLeave={() => {
              setDragging(false);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (!dropZoneBlocked) {
                setDragging(true);
              }
            }}
            onDrop={dropZoneBlocked ? undefined : onDrop}
            opacity={dropZoneBlocked ? 0.5 : 1}
            p={6}
            textAlign="center"
            transition="all 0.2s">
            <input
              accept={acceptAttr}
              disabled={disabled}
              multiple
              onChange={onInputChange}
              ref={inputRef}
              style={{ display: 'none' }}
              type="file"
            />
            <Box
              color={dragging ? 'blue.500' : 'gray.400'}
              mb={1}
              transition="color 0.2s">
              <Upload
                size="2xl"
                widthAuto
              />
            </Box>
            <Text
              color={dragging ? 'blue.500' : 'gray.400'}
              fontSize="sm"
              transition="color 0.2s">
              {dragging ? 'Drop to add' : hasStaged ? 'Add more files' : 'Click or drag files here'}
            </Text>
            {!hasStaged && Number.isFinite(autoCompressThresholdBytes) && (
              <Text
                color="gray.400"
                fontSize="sm"
                mt={1}>
                Files ≥ {formatFileSize(autoCompressThresholdBytes)} will be gzipped automatically
              </Text>
            )}
          </Box>
        )}

        {rejectedNames.length > 0 && (
          <Alert
            bg="orange.50"
            borderRadius="lg"
            color="orange.700"
            fontSize="sm"
            mb={3}
            status="warning">
            <Flex
              align="center"
              color="orange.400"
              gap={2}
              justify="center">
              <CircleExclamation widthAuto />
              <AlertDescription>
                Rejected — unsupported type:{' '}
                <Text
                  as="span"
                  fontFamily="mono"
                  fontSize="sm">
                  {rejectedNames.join(', ')}
                </Text>
              </AlertDescription>
            </Flex>
          </Alert>
        )}

        {hasStaged && (
          <VStack
            align="stretch"
            mb={4}
            spacing={2}>
            {staged.map((stagedFile, i) => {
              return (
                <StagedFileRow
                  disabled={disabled}
                  fileIndex={i + 1}
                  filenameSuffixLabel={filenameSuffixLabel}
                  key={stagedFile.id}
                  onRemove={removeStaged}
                  onSuffixChange={handleSuffixChange}
                  outputFilenameTemplate={outputFilenameTemplate}
                  progress={progressMap[stagedFile.id] ?? null}
                  showSuffixInput={showSuffixInput}
                  staged={stagedFile}
                  status={status}
                  totalFiles={staged.length}
                />
              );
            })}
          </VStack>
        )}

        {isIdle && hasStaged && (
          <HStack
            mb={4}
            spacing={2}>
            {compressCount > 0 && (
              <Badge
                borderColor="gray.600"
                borderRadius="full"
                borderWidth={1}
                colorScheme="green"
                fontSize="10px"
                px={1.5}
                variant="subtle">
                {compressCount} will compress
              </Badge>
            )}
            {rawCount > 0 && (
              <Badge
                borderColor="gray.600"
                borderRadius="full"
                borderWidth={1}
                colorScheme="blue"
                fontSize="10px"
                px={1.5}
                variant="subtle">
                {rawCount} raw
              </Badge>
            )}
          </HStack>
        )}

        {isDone && processed > 0 && isDefined(compressionSummary) && (
          <>
            <Divider
              borderColor="gray.200"
              mb={3}
            />
            <VStack
              align="stretch"
              mb={4}
              spacing={1}>
              <Flex
                align="baseline"
                justify="space-between">
                <Text
                  color="gray.500"
                  fontSize="sm"
                  letterSpacing="wider"
                  textTransform="uppercase">
                  Files uploaded
                </Text>
                <Text
                  color="gray.700"
                  fontSize="sm"
                  fontWeight="medium">
                  {processed}
                </Text>
              </Flex>
              {compressionSummary.compressedCount > 0 && (
                <>
                  <Flex
                    align="baseline"
                    justify="space-between">
                    <Text
                      color="gray.500"
                      fontSize="sm"
                      letterSpacing="wider"
                      textTransform="uppercase">
                      Compressed
                    </Text>
                    <Text
                      color="gray.700"
                      fontSize="sm"
                      fontWeight="medium">
                      {compressionSummary.compressedCount} of {processed}
                    </Text>
                  </Flex>
                  <Flex
                    align="baseline"
                    justify="space-between">
                    <Text
                      color="gray.500"
                      fontSize="sm"
                      letterSpacing="wider"
                      textTransform="uppercase">
                      Total size
                    </Text>
                    <Flex
                      align="baseline"
                      color="gray.700"
                      fontSize="sm"
                      fontWeight="medium"
                      gap={1}>
                      <Text>{formatFileSize(compressionSummary.totalOriginalBytes)}</Text>
                      <ArrowRight
                        size="xs"
                        widthAuto
                      />
                      <Text>{formatFileSize(compressionSummary.totalFinalBytes)}</Text>
                    </Flex>
                  </Flex>
                  {isDefined(compressionSummary.totalReductionPct) && (
                    <Flex
                      align="baseline"
                      justify="space-between">
                      <Text
                        color="gray.500"
                        fontSize="sm"
                        letterSpacing="wider"
                        textTransform="uppercase">
                        Reduction
                      </Text>
                      <Text
                        color="blue.600"
                        fontFamily="mono"
                        fontSize="sm"
                        fontWeight="bold">
                        {compressionSummary.totalReductionPct}%
                      </Text>
                    </Flex>
                  )}
                </>
              )}
            </VStack>
          </>
        )}

        {isDefined(error) && (
          <Alert
            bg="red.50"
            borderRadius="lg"
            color="red.700"
            mb={4}
            status="error">
            <Flex
              align="center"
              color="red.400"
              gap={2}
              justify="center">
              <CircleExclamation widthAuto />
              <AlertDescription fontSize="sm">{error}</AlertDescription>
            </Flex>
          </Alert>
        )}

        <HStack
          mt={2}
          spacing={3}>
          {showUploadButton && (isIdle || isCancelled) && hasStaged && (
            <DefaultToolTip
              isDisabled={!uploadBlocked}
              label="Fix duplicate values before uploading"
              placement="top">
              <Button
                colorScheme="blue"
                isDisabled={disabled || uploadBlocked}
                onClick={handleUpload}
                size="md">
                Upload {staged.length} file{staged.length > 1 ? 's' : ''}
              </Button>
            </DefaultToolTip>
          )}
          {isCompressing && (
            <>
              <Button
                colorScheme="blue"
                disabled
                isLoading
                loadingText="Processing…"
                size="md">
                Processing…
              </Button>
              {hasCompressibleFiles && (
                <Button
                  colorScheme="red"
                  onClick={cancelUpload}
                  size="md"
                  variant="outline">
                  Cancel
                </Button>
              )}
            </>
          )}
          {(isDone || isDefined(error)) && (
            <Button
              colorScheme="gray"
              isDisabled={disabled}
              onClick={reset}
              size="md"
              variant="outline">
              Start over
            </Button>
          )}
          {showUploadButton && (isIdle || isCancelled) && hasStaged && (
            <Button
              colorScheme="gray"
              isDisabled={disabled}
              onClick={reset}
              size="md"
              variant="outline">
              Clear all
            </Button>
          )}
        </HStack>
      </Box>
    );
  }
);

Uploader.displayName = 'Uploader';
